import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CategoryCard from '../components/CategoryCard';
import BusinessCard from '../components/BusinessCard';
import SEO from '../components/SEO';
import { Search, MapPin, Star, Store, Phone, Navigation } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [topBusinesses, setTopBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first if needed
      await axios.post(`${API}/seed`).catch(() => {});
      
      const [catRes, bizRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/businesses/top-rated?limit=6`)
      ]);
      setCategories(catRes.data);
      setTopBusinesses(bizRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const features = [
    {
      icon: <div className="w-16 h-16 mx-auto mb-3 bg-blue-50 rounded-full flex items-center justify-center">
        <Phone className="h-8 w-8 text-blue-600" />
        <Navigation className="h-6 w-6 text-[#E85A24] -ml-2" />
      </div>,
      title: t('callLocate'),
      description: t('callLocateDesc')
    },
    {
      icon: <div className="w-16 h-16 mx-auto mb-3 bg-yellow-50 rounded-full flex items-center justify-center">
        <Star className="h-10 w-10 text-yellow-500 fill-yellow-400" />
      </div>,
      title: t('topRated'),
      description: t('topRatedDesc')
    },
    {
      icon: <div className="w-16 h-16 mx-auto mb-3 bg-green-50 rounded-full flex items-center justify-center">
        <Store className="h-8 w-8 text-green-600" />
      </div>,
      title: 'Verified Local Shops',
      description: 'Browse curated and trusted businesses in your area'
    }
  ];

  // Structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "United Hands Karur Shop",
    "url": "https://yourwebsite.com/",
    "description": "Local business directory and shop listings for Karur",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://yourwebsite.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <SEO
        title="United Hands Karur Shop | Discover Best Local Businesses & Shops in Karur"
        description="Find trusted local businesses and shops in Karur. Browse categories, read reviews, compare shops, and connect with verified businesses. Your complete guide to shopping in Karur, Tamil Nadu."
        keywords="Karur shops, Karur businesses, local business directory, Karur shopping, shop listings, business reviews, Karur stores, Tamil Nadu businesses, local services Karur"
        structuredData={structuredData}
      />
      
      {/* Hero Section - Enhanced Modern Design */}
      <section className="relative min-h-[700px] bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 overflow-hidden animate-gradient">
        {/* Multi-layer animated background */}
        <div className="absolute inset-0">
          {/* Gradient orbs */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl animate-float"></div>
            <div className="absolute top-20 right-20 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl animate-floatSlow"></div>
            <div className="absolute -bottom-20 left-1/3 w-96 h-96 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-40 right-1/4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl animate-floatSlow" style={{animationDelay: '2s'}}></div>
          </div>
          
          {/* Geometric pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        {/* Animated floating shop icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Store className="absolute top-24 left-[8%] w-12 h-12 text-white/15 animate-float" />
          <Store className="absolute top-40 right-[12%] w-16 h-16 text-white/10 animate-floatSlow" />
          <Star className="absolute top-60 left-[15%] w-10 h-10 text-yellow-300/20 fill-yellow-300/20 animate-float" style={{animationDelay: '0.5s'}} />
          <MapPin className="absolute bottom-40 right-[18%] w-14 h-14 text-[#E85A24]/20 animate-floatSlow" style={{animationDelay: '1s'}} />
          <Phone className="absolute bottom-60 left-[25%] w-10 h-10 text-white/15 animate-float" style={{animationDelay: '1.5s'}} />
          
          {/* Decorative shapes */}
          <div className="absolute top-32 left-[20%] w-20 h-20 border-4 border-white/15 rounded-2xl rotate-12 animate-floatSlow"></div>
          <div className="absolute top-48 right-[22%] w-16 h-16 border-4 border-cyan-300/20 rounded-full animate-float" style={{animationDelay: '0.7s'}}></div>
          <div className="absolute bottom-44 left-[12%] w-24 h-24 border-4 border-pink-300/20 rounded-lg -rotate-12 animate-floatSlow" style={{animationDelay: '1.2s'}}></div>
          <div className="absolute top-56 right-[30%] w-12 h-12 bg-white/10 rounded-full animate-float" style={{animationDelay: '1.8s'}}></div>
          <div className="absolute bottom-52 right-[8%] w-8 h-8 bg-yellow-400/20 rounded-full animate-floatSlow" style={{animationDelay: '2.2s'}}></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 pt-12 pb-28 text-center">
          {/* Premium Location Badge */}
          <div className="inline-flex items-center gap-2.5 bg-gradient-to-r from-white/25 to-white/15 backdrop-blur-lg px-6 py-3 rounded-full text-white mb-8 shadow-2xl animate-bounce-in border border-white/40 hover:scale-105 transition-transform duration-300">
            <div className="relative">
              <MapPin className="h-5 w-5 text-[#E85A24] animate-pulse" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#E85A24] rounded-full animate-ping"></div>
            </div>
            <span className="font-bold text-sm tracking-wide">{t('location')}</span>
            <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
            <span className="text-xs text-white/80">Live Now</span>
          </div>

          {/* Enhanced Hero Title with gradient text */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-white mb-6 font-['Manrope'] max-w-5xl mx-auto leading-tight animate-fadeInUp drop-shadow-2xl">
            {t('heroTitle')}
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl text-white/90 mb-12 max-w-2xl mx-auto font-medium animate-fadeInUp" style={{animationDelay: '0.1s'}}>
            Find Hotels, Restaurants, Shops & Services Near You in Karur
          </p>

          {/* Enhanced Search Bar with glow effect */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-10 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 via-pink-400 to-yellow-400 rounded-full opacity-30 blur-xl group-hover:opacity-50 transition duration-500 animate-pulse"></div>
              
              <div className="relative flex items-center bg-white rounded-full shadow-2xl overflow-hidden">
                <div className="relative flex-1">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 z-10">
                    <Search className="h-5 w-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  </div>
                  <Input
                    type="text"
                    placeholder={t('searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-14 pl-14 pr-6 border-0 text-base placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-l-full bg-transparent"
                    data-testid="search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-14 px-10 rounded-r-full bg-gradient-to-r from-[#E85A24] via-orange-500 to-orange-600 hover:from-[#d14918] hover:via-orange-600 hover:to-orange-700 text-white font-bold text-base shadow-xl relative overflow-hidden group/btn transition-all duration-300 hover:scale-105"
                  data-testid="search-btn"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {t('search')}
                    <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700"></div>
                </Button>
              </div>
            </div>
          </form>
          
          {/* Enhanced Quick stats with cards */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-white animate-fadeInUp" style={{animationDelay: '0.4s'}}>
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-lg">
                <Store className="h-5 w-5 text-white" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-black">{topBusinesses.length}+</div>
                <div className="text-xs text-white/80 font-medium">Verified Shops</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <Star className="h-5 w-5 text-white fill-white" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-black">4.8</div>
                <div className="text-xs text-white/80 font-medium">Avg Rating</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-white/15 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 hover:bg-white/25 transition-all duration-300 hover:scale-105 shadow-lg">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-black">100%</div>
                <div className="text-xs text-white/80 font-medium">Local Businesses</div>
              </div>
            </div>
          </div>
          
          {/* Trust indicators */}
          <div className="mt-8 flex items-center justify-center gap-6 text-white/70 text-sm animate-fadeInUp" style={{animationDelay: '0.5s'}}>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>100% Free</span>
            </div>
            <div className="w-1 h-1 bg-white/40 rounded-full"></div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Verified Listings</span>
            </div>
            <div className="w-1 h-1 bg-white/40 rounded-full"></div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Real Reviews</span>
            </div>
          </div>
        </div>

        {/* Enhanced Animated Wave decoration with multiple layers */}
        <div className="absolute bottom-0 left-0 right-0">
          {/* Wave layer 1 */}
          <svg viewBox="0 0 1440 120" className="w-full h-auto fill-slate-50/20 absolute bottom-0">
            <path d="M0,32L80,42.7C160,53,320,75,480,80C640,85,800,75,960,69.3C1120,64,1280,64,1360,64L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z">
              <animate attributeName="d" dur="8s" repeatCount="indefinite"
                values="M0,32L80,42.7C160,53,320,75,480,80C640,85,800,75,960,69.3C1120,64,1280,64,1360,64L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z;
                        M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z;
                        M0,32L80,42.7C160,53,320,75,480,80C640,85,800,75,960,69.3C1120,64,1280,64,1360,64L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" />
            </path>
          </svg>
          
          {/* Wave layer 2 - Main */}
          <svg viewBox="0 0 1440 120" className="w-full h-auto fill-slate-50 drop-shadow-2xl relative z-10">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z">
              <animate attributeName="d" dur="10s" repeatCount="indefinite"
                values="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z;
                        M0,32L80,42.7C160,53,320,75,480,80C640,85,800,75,960,69.3C1120,64,1280,64,1360,64L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z;
                        M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" />
            </path>
          </svg>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-slate-50 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-blue-100 rounded-full filter blur-3xl opacity-30 animate-floatSlow"></div>
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-purple-100 rounded-full filter blur-3xl opacity-30 animate-float"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="flex items-center justify-between mb-10 animate-fadeInUp">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-['Manrope'] mb-2">
                {t('categories')}
              </h2>
              <div className="h-1.5 w-20 bg-gradient-to-r from-[#E85A24] to-orange-500 rounded-full"></div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/categories')}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-semibold btn-scale group"
              data-testid="view-all-categories"
            >
              {t('viewAll')}
              <span className="inline-block ml-1 transform group-hover:translate-x-1 transition-transform">→</span>
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-shimmer bg-white rounded-xl h-64 shadow-md" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {categories.slice(0, 6).map((category, index) => (
                <div key={category.id} className="stagger-item">
                  <CategoryCard category={category} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100 to-transparent rounded-full filter blur-3xl opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-orange-100 to-transparent rounded-full filter blur-3xl opacity-40"></div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 font-['Manrope']">
              {t('discoverTitle')}
            </h2>
            <div className="h-1.5 w-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="stagger-item text-center p-8 bg-gradient-to-br from-white to-slate-50 rounded-2xl hover:shadow-2xl transition-all duration-500 border border-slate-100 card-hover group"
                data-testid={`feature-${index}`}
              >
                <div className="transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-xl text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Decorative bottom accent */}
                <div className="mt-4 h-1 w-0 group-hover:w-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto transition-all duration-500"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Rated Section */}
      {topBusinesses.length > 0 && (
        <section className="py-20 bg-slate-50 relative overflow-hidden">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-5 hero-pattern"></div>
          
          <div className="container mx-auto px-4 md:px-6 relative z-10">
            <div className="flex items-center justify-between mb-10 animate-fadeInUp">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Star className="h-8 w-8 text-yellow-400 fill-yellow-400 animate-pulse" />
                  <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 font-['Manrope']">
                    {t('topRated')}
                  </h2>
                </div>
                <div className="h-1.5 w-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {topBusinesses.map((business, index) => (
                <div key={business.id} className="stagger-item">
                  <BusinessCard business={business} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Hidden for manual shop approval only */}
      {/* <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-800 relative overflow-hidden animate-gradient">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-pink-300 rounded-full filter blur-3xl animate-floatSlow"></div>
        </div>
        
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <Store className="absolute top-20 left-[10%] w-16 h-16 text-white/10 animate-float" />
          <Star className="absolute top-32 right-[15%] w-12 h-12 text-yellow-400/20 animate-floatSlow" />
          <Phone className="absolute bottom-20 left-[20%] w-14 h-14 text-[#E85A24]/20 animate-float" style={{animationDelay: '1s'}} />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-white mb-6 animate-bounce-in">
              <Store className="h-5 w-5 text-[#E85A24]" />
              <span className="font-semibold">For Business Owners</span>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 font-['Manrope'] animate-fadeInUp drop-shadow-lg">
              {t('growBusiness')}
            </h2>
            <p className="text-blue-50 text-lg mb-10 max-w-2xl mx-auto leading-relaxed animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              {t('growBusinessDesc')}
            </p>
            
            <div className="animate-fadeInUp" style={{animationDelay: '0.4s'}}>
              <Button
                onClick={() => navigate('/add-shop')}
                className="bg-gradient-to-r from-[#E85A24] to-orange-600 hover:from-[#d14918] hover:to-orange-700 text-white rounded-full px-12 py-7 text-xl font-bold shadow-2xl btn-scale ripple group"
                data-testid="cta-add-shop"
              >
                <span className="flex items-center gap-3">
                  {t('addYourShop')}
                  <svg className="w-6 h-6 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Button>
            </div>
            
            <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-white/80 text-sm animate-fadeInUp" style={{animationDelay: '0.6s'}}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free to list</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Easy setup</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Reach more customers</span>
              </div>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default HomePage;
